import { authService } from '../services/authService';
import React, { useState, useEffect } from 'react';
import { ResponsiveNavigation } from '../components/ResponsiveNavigation';
import { useSearchParams } from 'react-router-dom';
import { PlatformMenu, PlatformShellContent, usePlatformNavigation } from '../components/platform/PlatformControls';
import { adminApi } from '../services/adminApi';
import { getStoredTheme, applyTheme, type Theme } from '../shared/themeUtils';

import { TenantsManagement } from './components/TenantsManagement';
import { SubscriptionsManagement } from './components/SubscriptionsManagement';
import { UsageDashboard } from './components/UsageDashboard';
import { AuditLog } from './components/AuditLog';
import { SystemSettings } from './components/SystemSettings';
import { ProfileSection } from './components/ProfileSection';
import { MobileEnrollmentQrPanel } from './components/MobileEnrollmentQrPanel';
import { SmsGatewayManagement } from './components/SmsGatewayManagement';
import { EmailAllocationsManagement } from './components/EmailAllocationsManagement';
import { PortalBanner } from '../components/PortalBanner';
import { NotificationDeliveryIncidentBanner } from '../components/NotificationDeliveryIncidentBanner';
import { loadPortalBannerConfig, onPortalBannersChanged, resolvePortalBanners, type PortalBannerConfig } from '../shared/portalBannerStore';

import './SuperAdminPortal.css';

type AdminView = 'tenants' | 'subscriptions' | 'smsGateways' | 'emailAllocations' | 'usage' | 'audit' | 'settings' | 'mobileEnrollment' | 'profile';

const VIEW_TITLES: Record<AdminView, string> = {
  tenants: 'Tenant Management',
  subscriptions: 'Subscriptions',
  smsGateways: 'SMS Gateways',
  emailAllocations: 'Email Allocations',
  usage: 'Usage Dashboard',
  audit: 'Audit Log',
  settings: 'System Settings',
  mobileEnrollment: 'Mobile Enrollment',
  profile: 'Profile Settings',
};

export const SuperAdminPortal: React.FC = () => {
  useEffect(() => authService.subscribe(() => setUser(authService.getState().user)), []);
  const [platformParams, setPlatformParams] = useSearchParams();
  const leavePlatform = usePlatformNavigation();
  const [activeView, setActiveView] = useState<AdminView>(() => platformParams.has('platformAdminTab') ? 'settings' : 'tenants');
  useEffect(() => { if (platformParams.has('platformAdminTab')) setActiveView('settings'); }, [platformParams]);
  const [user, setUser] = useState<any>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [theme, setTheme] = useState<Theme>(getStoredTheme);
  const [bannerConfig, setBannerConfig] = useState<PortalBannerConfig>(() => loadPortalBannerConfig());
  const [dismissedBannerIds, setDismissedBannerIds] = useState<string[]>([]);

  useEffect(() => { applyTheme(theme); }, [theme]);

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    return onPortalBannersChanged((config) => {
      setBannerConfig(config);
      setDismissedBannerIds([]);
    });
  }, []);

  const loadUser = async () => {
    try {
      const currentUser = await adminApi.getCurrentUser();
      setUser(currentUser);
    } catch (err) {
      console.error('Failed to load user:', err);
    }
  };

  const handleLogout = async () => {
    try {
      await adminApi.logout();
    } finally {
      window.location.href = '/admin';
    }
  };

  const getInitials = (u: any): string => {
    if (!u) return '?';
    const name: string = u.displayName || u.email || '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  const renderView = () => {
    switch (activeView) {
      case 'tenants':
        return <TenantsManagement />;
      case 'subscriptions':
        return <SubscriptionsManagement />;
      case 'smsGateways':
        return <SmsGatewayManagement />;
      case 'emailAllocations':
        return <EmailAllocationsManagement />;
      case 'usage':
        return <UsageDashboard />;
      case 'audit':
        return <AuditLog />;
      case 'settings':
        return <SystemSettings />;
      case 'mobileEnrollment':
        return <MobileEnrollmentQrPanel />;
      case 'profile':
        return <ProfileSection user={user} onUserUpdated={loadUser} />;
      default:
        return null;
    }
  };

  const navItem = (view: AdminView, icon: string, label: string) => (
    <button
      key={view}
      className={`sa-nav-item${activeView === view ? ' active' : ''}`}
      onClick={() => {
        setActiveView(view);
        if (view === 'settings') { const next = new URLSearchParams(platformParams); next.delete('platformView'); next.set('platformAdminTab', 'releases'); setPlatformParams(next); }
      }}
    >
      <span className="icon">{icon}</span>
      {label}
    </button>
  );

  const portalBanners = resolvePortalBanners(bannerConfig, { audience: 'all' }).filter((item) => !dismissedBannerIds.includes(item.id));

  return (
    <div className="super-admin-portal" onClickCapture={event => { if ((event.target as HTMLElement).closest('.sa-nav-item, .user-menu-item')) leavePlatform(); }} onClick={() => setShowUserMenu(false)}>
      {/* ── Left Sidebar ──────────────────────────────────────── */}
      <ResponsiveNavigation sidebarClassName="sa-sidebar">
        <div className="sa-sidebar-header">
          <div className="sa-logo">Z</div>
          <div className="sa-sidebar-title">
            <span className="sa-brand-name">Zurvit</span>
            <span className="sa-brand-label">Platform Admin</span>
          </div>
        </div>

        <div className="sa-nav-group">
          <div className="sa-nav-label">Management</div>
          {navItem('tenants', '\uD83C\uDFE2', 'Tenants')}
          {navItem('subscriptions', '\uD83D\uDCB3', 'Subscriptions')}
          {navItem('smsGateways', '\uD83D\uDCF2', 'SMS Gateways')}
          {navItem('emailAllocations', '\u2709\uFE0F', 'Email Allocations')}
        </div>

        <div className="sa-nav-group">
          <div className="sa-nav-label">Monitoring</div>
          {navItem('usage', '\uD83D\uDCCA', 'Usage Dashboard')}
          {navItem('audit', '\uD83D\uDDD2\uFE0F', 'Audit Log')}
        </div>

        <div className="sa-sidebar-footer">
          {navItem('settings', '\u2699\uFE0F', 'System Settings')}
          {navItem('mobileEnrollment', '\uD83D\uDCF1', 'Mobile Enrollment')}
          {navItem('profile', '\uD83D\uDC64', 'My Profile')}
        </div>
      </ResponsiveNavigation>

      {/* ── Main Content ──────────────────────────────────────── */}
      <div className="sa-main">
        <header className="sa-top-bar">
          <h1 className="sa-view-title">{VIEW_TITLES[activeView]}</h1>

          <div className="sa-top-bar-actions">
            {user ? (
              <div className="user-profile" onClick={(e) => { e.stopPropagation(); setShowUserMenu(v => !v); }}>
                <div className="avatar">{getInitials(user)}</div>
                <div className="user-info">
                  <span className="user-name">{user.displayName || user.email}</span>
                  <span className="user-role">Platform Admin</span>
                </div>
                <span className="chevron">{showUserMenu ? '\u25B2' : '\u25BC'}</span>
                {showUserMenu && (
                  <div className="user-menu">
                    <div className="user-menu-header">
                      <div className="avatar avatar-lg">{getInitials(user)}</div>
                      <div>
                        <div className="user-menu-name">{user.displayName || '\u2014'}</div>
                        <div className="user-menu-email">{user.email}</div>
                      </div>
                    </div>
                    <hr className="user-menu-divider" />
                    <button className="user-menu-item" onClick={() => { setActiveView('profile'); setShowUserMenu(false); }}>
                      Profile Settings
                    </button>
                    <button className="user-menu-item" onClick={() => { setTheme(t => t === 'light' ? 'dark' : 'light'); }}>
                      {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
                    </button>
                    <hr className="user-menu-divider" />
                    <button className="user-menu-item user-menu-signout" onClick={handleLogout}>
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="avatar avatar-placeholder">?</div>
            )}
            <PlatformMenu />
          </div>
        </header>

        <PortalBanner
          items={portalBanners}
          audience="all"
          onDismiss={(id) => setDismissedBannerIds((prev) => (prev.includes(id) ? prev : [...prev, id]))}
        />
        <NotificationDeliveryIncidentBanner audience="platform" />

        <main className="sa-content">
          <PlatformShellContent portal="superadmin" page={activeView}>{renderView()}</PlatformShellContent>
        </main>
      </div>
    </div>
  );
};
